import {
  yupArray,
  yupBoolean,
  yupDateString,
  yupEnum,
  yupNumber,
  yupObject,
  yupString,
} from 'shared/utils/YupUtils';
import { InferType } from 'yup';

const bankAccountVerificationStatuses = [
  'new',
  'pending',
  'verified',
  'failed',
  'errored',
  'locked',
  'in_review',
] as const;

export const verificationSteps = [
  'restricted',
  'not_started',
  'bank_account',
  'bank_account_verification',
  'verified',
  'bank_account_failed',
  'bank_account_errored',
] as const;

export type BankPaymentErrorDTO = InferType<typeof bankPaymentErrorSchema>;
const bankPaymentErrorSchema = yupObject({
  name: yupString(),
  code: yupString(),
  action: yupString(),
  description: yupString(),
});

export type BankAccountVerificationStatusType =
  (typeof bankAccountVerificationStatuses)[number];

const bankAccountVerificationMethods = ['ach', 'instant'] as const;

export type BankAccountVerificationMethodType =
  (typeof bankAccountVerificationMethods)[number];

export type SuperPayBankAccountDTO = InferType<typeof bankAccountSchema>;

const bankAccountSchema = yupObject({
  bank_name: yupString().nullable(true),
  display_number: yupString().nullable(true),
  created_at: yupDateString('DateISO'),
  is_ach_bank_different: yupBoolean(),
  microdeposit_sent_at: yupDateString('DateISO'),
  verification_available_date: yupDateString('DateISO'),
  is_verification_available: yupBoolean().required(),
  verification_status: yupEnum(bankAccountVerificationStatuses),
  verification_method: yupEnum(bankAccountVerificationMethods, null),
  verification_error_count: yupNumber().nullable(),
  payment_error: bankPaymentErrorSchema.nullable(),
});

export type SuperPaySettingsDTO = InferType<typeof superPaySettingsSchema>;
export const superPaySettingsSchema = yupObject({
  bank_accounts: yupArray(bankAccountSchema),
  verification_step: yupEnum(verificationSteps),
  hours_until_update_allowed: yupNumber(),
});

export type SuperPayInvitesShippersDTO = InferType<
  typeof superPayInvitesShippersSchema
>;
export const superPayInvitesShippersSchema = yupObject({
  shippers_to_invite: yupArray(
    yupObject({
      name: yupString(),
      guid: yupString(),
      loads_count: yupNumber(),
    }),
  ).defined(),
});

export const instantTransferSettleDialogSchema = yupObject({
  status: yupEnum(['info', 'warning', 'past_due', 'failed'] as const, null),
  bank_account: yupString().nullable(),
  loads: yupArray(
    yupObject({
      load_id: yupString(),
      load_guid: yupString(),
      price: yupNumber(),
    }),
  ).nullable(),
});

export type InstantTransferSettleDialogDTO = InferType<
  typeof instantTransferSettleDialogSchema
>;

export type SuperPayInstantTransferDialogDTO = InferType<
  typeof superPayInstantTransferDialogSchema
>;

export const superPayInstantTransferDialogSchema = yupObject({
  status: yupEnum(['available', 'expired'] as const, 'available'),
  load_id: yupString(),
  load_guid: yupString(),
  origin: yupString().nullable().optional(),
  destination: yupString().nullable().optional(),
  vehicles: yupArray(yupString()).nullable().optional(),
  price: yupNumber().nullable().optional(),
  expected_date: yupDateString('DateTimeISO').optional(),
  new_price: yupNumber().optional(),
  fee_percentage: yupNumber().optional(),
  fee_amount: yupNumber().optional(),
  bank_account: yupString().nullable().optional(),
  settlement: instantTransferSettleDialogSchema.nullable().optional(),
});

export type SuperPayInstantTransferConfirmDTO = InferType<
  typeof superPayInstantTransferConfirmSchema
>;

export const superPayInstantTransferConfirmSchema = yupObject({
  status: yupEnum(
    ['pending', 'completed', 'failed', 'unavailable'] as const,
    'completed',
  ),
  load_id: yupString(),
  load_guid: yupString(),
  price: yupNumber(),
  sent_date: yupDateString('DateTimeISO'),
  new_price: yupNumber(),
  fee_percentage: yupNumber(),
  fee_amount: yupNumber(),
  bank_account: yupString(),
});

export const instantTransferStatisticsSchema = yupObject({
  amount: yupNumber().nullable(false),
  loads_count: yupNumber().nullable(false),
});

export type InstantTransferStatisticsSchemaDTO = InferType<
  typeof instantTransferStatisticsSchema
>;
